import * as React from "react";
import { Button, Typography, Box, Grid, Divider, Stack, IconButton, Icon, Dialog, useMediaQuery, DialogContent } from "@mui/material";
import { makeStyles } from "@mui/styles";
import NumberFormat from "react-number-format";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { getBalanceDetails, getCancelOTP, getRefundOTP, getTransactionHistory, selectPaymentState } from "../../resui/payments/sagas/residentpaymentsSlice";
import SpinnerModal from "./SpinnerModal";
import SpinnerWithNoConfirmation from "./spinnerWithNoConfirmation";
import CancelIcon from "../../icons/cancelIcon.svg";
import { RefundDto, ReturnTransactionDto, ValidTransactionStatusEnum } from "@inosio/payments-sdk";
import PaymentConfirmation from "./PaymentConfirmation";
import { convertSqlDateToUSCalendarDate } from "../util/converters";
import ExportIcon from "../../icons/exportIcon2.svg";
import { getExportTransDetails } from "../../pmui/transactions/sagas/pmTransactionsSlice";

const useStyles = makeStyles({
  modalContainer: {
    background: "#FFFFFF",
    boxShadow: "none",
    borderRadius: "24px",
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "33px",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "10%",
  },
  reviewAndSubmitMainDetails: {
    marginBottom: "18px",
  },
});

interface paymentModalProps {
  canCancel: boolean;
  closeCallback: (event: object, reason: string) => void;
  deviceWidth: number;
  fee: number;
  leaseHolderName: string;
  last4: string;
  open: boolean;
  paidAmount: number;
  paymentID: string;
  pmCompany: string;
  propertyName: string;
  transactionTypeId: number;
  transactionMemo?: string;
  refund?: RefundDto;
  transactionDate?: string;
  transactionStatus?: ValidTransactionStatusEnum;
  returnTransaction?: ReturnTransactionDto;
}

export default function PaymentHistoryDetailsModal(props: paymentModalProps) {
  const classes = useStyles();
  const dialogSize = {
    left: 7,
    right: 5,
  };
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const handleOpenConfirmation = () => setOpenConfirmation(true);

  const handleCloseConfirmation = () => setOpenConfirmation(false);
  const dispatch = useDispatch();
  const handleCloseCancelOTPSpinnerModal = React.useCallback(() => {
    setIsCancelOTPSpinnerModalOpen(false);
  }, []);

  const handleCloseRefundOTPSpinnerModal = React.useCallback(() => {
    setIsRefundOTPSpinnerModalOpen(false);
  }, []);

  const { isCancelOTPError, isCancelOTPSuccess, cancelOTPError, isCancelStatusFetching } = useSelector(selectPaymentState);
  const [globalCancelOTPError, setGlobalCancelOTPError] = React.useState<string | null>(null);
  const [isCancelOTPSpinnerModalOpen, setIsCancelOTPSpinnerModalOpen] = React.useState(false);
  const [cancelOTPSpinnerStatus, setCancelOTPSpinnerStatus] = React.useState<string | null>(null);
  const { isRefundOTPError, isRefundOTPSuccess, refundOTPError } = useSelector(selectPaymentState);
  const [globalRefundOTPError, setGlobalRefundOTPError] = React.useState<string | null>(null);
  const [isRefundOTPSpinnerModalOpen, setIsRefundOTPSpinnerModalOpen] = React.useState(false);
  const [refundOTPSpinnerStatus, setRefundOTPSpinnerStatus] = React.useState<string | null>(null);

  const refreshTransactionHistory = React.useCallback(() => {
    dispatch(getTransactionHistory());
  }, [dispatch]);

  const resetGlobalErrors = React.useCallback(() => {
    setGlobalRefundOTPError(null);
    setGlobalCancelOTPError(null);
  }, [setGlobalRefundOTPError, setGlobalCancelOTPError]);

  // cancel OTP success
  React.useEffect(() => {
    if (isCancelOTPSuccess) {
      setCancelOTPSpinnerStatus("stop");
      refreshTransactionHistory();
      dispatch(getBalanceDetails()); //we need to refresh the pending payments on balance title
    }
  }, [isCancelOTPSuccess, refreshTransactionHistory]);

  // cancel OTP error
  React.useEffect(() => {
    if (isCancelOTPError) {
      handleCloseCancelOTPSpinnerModal();
      setGlobalCancelOTPError(cancelOTPError);
    }
  }, [isCancelOTPError, cancelOTPError, setGlobalCancelOTPError, handleCloseCancelOTPSpinnerModal]);

  // refund OTP success
  React.useEffect(() => {
    if (isRefundOTPSuccess) {
      setRefundOTPSpinnerStatus("stop");
      refreshTransactionHistory();
    }
  }, [isRefundOTPSuccess, refreshTransactionHistory]);

  // refund OTP error
  React.useEffect(() => {
    if (isRefundOTPError) {
      handleCloseRefundOTPSpinnerModal();
      setGlobalRefundOTPError(refundOTPError);
    }
  }, [isRefundOTPError, refundOTPError, setGlobalRefundOTPError, handleCloseRefundOTPSpinnerModal]);

  const handleCancelPaymentClick = () => {
    dispatch(getCancelOTP({ transactionUuid: props.paymentID }));
    setIsCancelOTPSpinnerModalOpen(true);
  };

  const handleRefundPaymentClick = () => {
    dispatch(getRefundOTP({ transactionUuid: props.paymentID, message: "I want a refund" }));
    setIsRefundOTPSpinnerModalOpen(true);
  };

  const handleModalClose = () => {
    resetGlobalErrors();
    return props.closeCallback({}, "closeDetailsModal");
  };

  const handleGetTransactionStatus = ()=>{     
    
    let refundStatus = props.refund?.Status;
    switch (props.transactionStatus) {
      case ValidTransactionStatusEnum.Pending:
      case ValidTransactionStatusEnum.Cancelled:
      case ValidTransactionStatusEnum.Chargedback:
        return props.transactionStatus;
      case ValidTransactionStatusEnum.Completed:
        //if Refund is Approved or Issued
        if (refundStatus && props.refund?.TargetedIssueDate) return `Refund ${refundStatus} - Issue Date ${convertSqlDateToUSCalendarDate(props.refund?.TargetedIssueDate)}`;
        //if Refund is Requested or denied
        if (refundStatus && ["Requested", "Denied"].includes(refundStatus)) return `Refund ${refundStatus}`;
        if(props.transactionTypeId === 8) return `${props.transactionStatus} - ${props.transactionMemo}`;
        //No refund
        else return props.transactionStatus;
      case ValidTransactionStatusEnum.Refunded:
        //if the transaction is refunded, show the issue date as well
        if (props.refund?.TargetedIssueDate) return `${props.transactionStatus} - Issue Date ${convertSqlDateToUSCalendarDate(props.refund?.TargetedIssueDate)}`;
        else return props.transactionStatus;
      case ValidTransactionStatusEnum.Returned:
        //if the transaction is returned show the reason as well
        if (props?.returnTransaction?.Reason) return `${props.transactionStatus} - ${props?.returnTransaction?.Reason}`;
        else return props.transactionStatus;
    }
  };

  const fullScreen = useMediaQuery("(max-width:600px)");
  return (
    <div className={classes.modalContainer}>
      {/* <Modal open={props.open} onClose={handleModalClose} aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description"> */}
      <Dialog
        fullScreen={fullScreen}
        // classes={{ paper: classes.paper }}
        open={props.open}
        onClose={handleModalClose}
        fullWidth={true}
        disableEnforceFocus
        style={{
          borderRadius: "24px",
        }}
        PaperProps={{
          style: {
            pointerEvents: "auto",
            height: !fullScreen ? "700px" : "100%",
            borderRadius: fullScreen ? "0px" : "24px"
          },
        }}
      >
        <DialogContent>
          {/* <Card sx={style}> */}
          {isCancelStatusFetching ? (
            <Grid item xs={12}>
              <Stack direction="column" justifyContent="center" alignItems="center" style={{ height: "50vh" }}>
                <SpinnerWithNoConfirmation />
              </Stack>
            </Grid>
          ) : (
            <>
              {globalCancelOTPError?.length ? (
                <div>
                  <Typography
                    style={{
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: "normal",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "18px",
                      color: "#DB3939",
                    }}
                  >
                    {globalCancelOTPError}
                  </Typography>
                </div>
              ) : (
                ""
              )}
              {globalRefundOTPError?.length ? (
                <div>
                  <Typography
                    style={{
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: "normal",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "18px",
                      color: "#DB3939",
                    }}
                  >
                    {globalRefundOTPError}
                  </Typography>
                </div>
              ) : (
                ""
              )}
              <Grid container>
                <Grid item xs={1}></Grid>
                <Grid item xs={9}>
                  <Typography id="payment-id" sx={{ mt: 2 }}>
                    Payment ID: {props.paymentID}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <IconButton sx={{ ml: "30px" }} onClick={handleModalClose}>
                    <Icon>
                      <img src={CancelIcon} alt="" width={24} height={24} />
                    </Icon>
                  </IconButton>
                </Grid>
              </Grid>
              <Typography id="payment-details-date">
                {format(new Date(props.transactionDate || ""), "EEEE, MMMM d, yyyy hh:mm a")}
                <br />
                {props.transactionStatus && handleGetTransactionStatus()}
              </Typography>
              <Box sx={{ width: "100%" }}>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                  <Grid item xs={dialogSize.left}>
                    <Typography
                      style={{
                        color: "#14142B",
                        fontSize: "16px",
                      }}
                      id="review-submit-main-details"
                    >
                      {" "}
                      {props.pmCompany ? `${props.pmCompany.slice(0, 20)}...` : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={dialogSize.right}>
                    <Typography
                      style={{
                        color: "#14142B",
                        fontSize: "16px",
                      }}
                      id="review-submit-main-details"
                    >
                      {props.propertyName ? `${props.propertyName.slice(0, 13)}...` : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={dialogSize.left}>
                    <div className={classes.reviewAndSubmitMainDetails}>
                      <Typography
                        style={{
                          color: "#14142B",
                          fontSize: "16px",
                        }}
                        id="review-submit-main-details"
                      >
                        {props.leaseHolderName}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={dialogSize.right}>
                    <div className={classes.reviewAndSubmitMainDetails}>
                      <Typography
                        style={{
                          color: "#14142B",
                          fontSize: "16px",
                        }}
                        id="review-submit-main-details"
                      >
                        {" "}
                        {/* TODO: Add Property Unit when avaialable */}
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
                <Divider />
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ marginTop: "5px" }}>
                  <Grid item xs={dialogSize.left}>
                    <Typography id="review-submit-sub-details"> Payment Method</Typography>
                  </Grid>
                  <Grid item xs={dialogSize.right}>
                    <Typography id="review-submit-sub-details">{props.last4}</Typography>
                  </Grid>
                  <Grid item xs={dialogSize.left}>
                    <Typography id="review-submit-sub-details">Amount</Typography>
                  </Grid>
                  <Grid item xs={dialogSize.right}>
                    <Typography id="review-submit-sub-details">
                      <NumberFormat thousandsGroupStyle="thousand" value={props.paidAmount} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
                    </Typography>
                  </Grid>
                  <Grid item xs={dialogSize.left}>
                    <Typography id="review-submit-sub-details">Fee Amount</Typography>
                  </Grid>
                  <Grid item xs={dialogSize.right}>
                    <Typography id="review-submit-sub-details">
                      <NumberFormat thousandsGroupStyle="thousand" value={props.fee} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
                    </Typography>
                  </Grid>
                  <Grid item xs={dialogSize.left}>
                    <Typography id="review-submit-sub-details">Total Amount</Typography>
                  </Grid>
                  <Grid item xs={dialogSize.right}>
                    <Typography id="review-submit-sub-details">
                      <NumberFormat
                        thousandsGroupStyle="thousand"
                        value={props.paidAmount + props.fee}
                        prefix="$"
                        decimalSeparator="."
                        displayType="text"
                        type="text"
                        thousandSeparator={true}
                        allowNegative={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={dialogSize.left}>
                    <Typography id="review-submit-sub-details">Transaction Date</Typography>
                  </Grid>
                  <Grid item xs={dialogSize.right}>
                    <Typography id="review-submit-sub-details">{format(new Date(props?.transactionDate || ""), "MM/dd/yyyy")}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <img
                      src={ExportIcon}
                      alt="close"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        dispatch({
                          type: getExportTransDetails.type,
                          payload: props.paymentID,
                        });
                      }}
                    />
                  </Grid>
                </Grid>
                <div className={classes.buttonContainer}>
                  {
                  props.transactionTypeId===8?<div></div> :
                  props.canCancel ? (
                    <Button
                      variant="contained"
                      color="error"
                      id="request-fund"
                      style={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: "normal",
                        fontWeight: 800,
                        backgroundColor: "#DB3939",
                      }}
                      onClick={handleOpenConfirmation}
                    >
                      Cancel Payment
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="error"
                      id="request-fund"
                      style={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: "normal",
                        fontWeight: 800,
                        backgroundColor: "#DB3939",
                      }}
                      onClick={handleRefundPaymentClick}
                      disabled={props.transactionTypeId===8 || !!props.refund || props.transactionStatus !== ValidTransactionStatusEnum.Completed}
                    >
                      Request refund
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    id="done"
                    style={{
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: "normal",
                      fontWeight: 700,
                      width: "100px",
                      backgroundColor: "#39A2DB",
                    }}
                    onClick={() => props.closeCallback({}, "closeDetailsModal")}
                  >
                    Done
                  </Button>
                </div>
              </Box>
              {isCancelOTPSpinnerModalOpen ? (
                <SpinnerModal
                  height={fullScreen ? "100%" : "700px"}
                  openSpinnerModal={isCancelOTPSpinnerModalOpen}
                  handleCloseSpinnerModal={handleCloseCancelOTPSpinnerModal}
                  spinnerStatus={cancelOTPSpinnerStatus}
                  closeCancel={() => props.closeCallback({}, "closeDetailsModal")}
                />
              ) : (
                <></>
              )}
              {isRefundOTPSpinnerModalOpen ? (
                <SpinnerModal
                  height={fullScreen ? "100%" : "700px"}
                  openSpinnerModal={isRefundOTPSpinnerModalOpen}
                  handleCloseSpinnerModal={handleCloseRefundOTPSpinnerModal}
                  spinnerStatus={refundOTPSpinnerStatus}
                  closeCancel={() => props.closeCallback({}, "closeDetailsModal")}
                />
              ) : (
                <></>
              )}
            </>
          )}
          {/* </Card> */}
        </DialogContent>
      </Dialog>
      {openConfirmation ? <PaymentConfirmation open={openConfirmation} onClose={handleCloseConfirmation} cancelTransaction={handleCancelPaymentClick} /> : <></>}
    </div>
  );
}
